// src/components/treinamentos/TreinamentoPopCard.js
import React, { useState } from "react";
import {
  FileCheck,
  AlertTriangle,
  CheckCircle2,
  Clock,
  User,
  Wrench,
  Calendar,
  Printer,
  Check
} from "lucide-react";

export default function TreinamentoPopCard({ pop }) {
  const [checkedSteps, setCheckedSteps] = useState({});

  if (!pop) return null;

  const toggleStep = (index) => {
    setCheckedSteps((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=850,height=950");
    if (!printWindow) {
      alert("Por favor, permita pop-ups no navegador para imprimir a ficha técnica do POP.");
      return;
    }

    const toolsFormatted = Array.isArray(pop.tools) ? pop.tools.join(", ") : pop.tools;
    const dateFormatted = new Date().toLocaleDateString("pt-BR");
    const timeFormatted = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

    const htmlContent = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <title>${pop.tag || "POP"} - ${pop.title}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 12mm 15mm 12mm 15mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #0f172a;
      line-height: 1.45;
      margin: 0;
      padding: 0;
      font-size: 12.5px;
      background: #ffffff;
    }
    .header {
      border-bottom: 2.5px solid #0f172a;
      padding-bottom: 10px;
      margin-bottom: 14px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    .company {
      font-size: 16px;
      font-weight: 900;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: #0f172a;
    }
    .company-sub {
      font-size: 10px;
      color: #475569;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-top: 2px;
    }
    .tag {
      background: #0f172a;
      color: #ffffff;
      padding: 4px 10px;
      border-radius: 4px;
      font-weight: 800;
      font-size: 12px;
      font-family: monospace;
      display: inline-block;
    }
    .title {
      font-size: 17px;
      font-weight: 800;
      color: #0f172a;
      margin-top: 6px;
      margin-bottom: 0;
      line-height: 1.25;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: 1.2fr 1fr 1.3fr;
      gap: 10px;
      margin-bottom: 14px;
    }
    .meta-box {
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      padding: 8px 10px;
      border-radius: 6px;
    }
    .meta-label {
      font-size: 9.5px;
      font-weight: 800;
      color: #475569;
      text-transform: uppercase;
      margin-bottom: 2px;
      letter-spacing: 0.5px;
    }
    .meta-value {
      font-size: 11.5px;
      font-weight: 600;
      color: #1e293b;
      line-height: 1.3;
    }
    .section-title {
      font-size: 12px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #0f172a;
      border-bottom: 1px solid #cbd5e1;
      padding-bottom: 4px;
      margin-top: 14px;
      margin-bottom: 10px;
    }
    .steps-list {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .step-item {
      display: flex;
      align-items: flex-start;
      margin-bottom: 7px;
      padding-bottom: 6px;
      border-bottom: 1px dashed #e2e8f0;
      page-break-inside: avoid;
    }
    .step-checkbox {
      width: 14px;
      height: 14px;
      border: 1.5px solid #475569;
      border-radius: 3px;
      margin-right: 10px;
      margin-top: 2px;
      flex-shrink: 0;
    }
    .step-num {
      font-weight: 800;
      font-family: monospace;
      color: #0f172a;
      margin-right: 8px;
      min-width: 18px;
    }
    .step-text {
      flex: 1;
      font-size: 11.5px;
      color: #1e293b;
      line-height: 1.4;
    }
    .callout-warning {
      background: #fffbeb;
      border: 1.5px solid #f59e0b;
      padding: 9px 12px;
      border-radius: 6px;
      margin-top: 12px;
      page-break-inside: avoid;
    }
    .callout-title-warn {
      font-size: 10.5px;
      font-weight: 900;
      color: #b45309;
      text-transform: uppercase;
      margin-bottom: 2px;
      letter-spacing: 0.5px;
    }
    .callout-success {
      background: #ecfdf5;
      border: 1.5px solid #10b981;
      padding: 9px 12px;
      border-radius: 6px;
      margin-top: 8px;
      page-break-inside: avoid;
    }
    .callout-title-succ {
      font-size: 10.5px;
      font-weight: 900;
      color: #047857;
      text-transform: uppercase;
      margin-bottom: 2px;
      letter-spacing: 0.5px;
    }
    .footer {
      margin-top: 20px;
      padding-top: 10px;
      border-top: 1px solid #cbd5e1;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      font-size: 9.5px;
      color: #64748b;
      page-break-inside: avoid;
    }
    .sig-block {
      border-top: 1px solid #94a3b8;
      width: 190px;
      text-align: center;
      padding-top: 4px;
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="company">Sempre Joias</div>
      <div class="company-sub">Manual de Operações &bull; Procedimento Operacional Padrão</div>
      <h1 class="title">${pop.title}</h1>
    </div>
    <div style="text-align: right;">
      <span class="tag">${pop.tag || "POP OPERACIONAL"}</span>
      <div style="font-size: 9.5px; color: #64748b; margin-top: 4px; font-weight: 600;">Via Impressa de Bancada</div>
    </div>
  </div>

  <div class="meta-grid">
    <div class="meta-box">
      <div class="meta-label">Quando Usar</div>
      <div class="meta-value">${pop.when_to_use}</div>
    </div>
    <div class="meta-box">
      <div class="meta-label">Responsável</div>
      <div class="meta-value">${pop.responsible}</div>
    </div>
    <div class="meta-box">
      <div class="meta-label">Ferramentas / Insumos</div>
      <div class="meta-value">${toolsFormatted}</div>
    </div>
  </div>

  <div class="section-title">Passo a Passo de Execução</div>
  <div class="steps-list">
    ${(pop.steps || [])
      .map(
        (step, idx) => `
      <div class="step-item">
        <div class="step-checkbox"></div>
        <div class="step-num">${idx + 1}.</div>
        <div class="step-text">${step}</div>
      </div>
    `
      )
      .join("")}
  </div>

  ${
    pop.critical_points
      ? `
    <div class="callout-warning">
      <div class="callout-title-warn">⚠️ Ponto Crítico de Atenção / Risco Operacional</div>
      <div style="font-size: 11px; color: #92400e; line-height: 1.35;">${pop.critical_points}</div>
    </div>
  `
      : ""
  }

  ${
    pop.expected_result
      ? `
    <div class="callout-success">
      <div class="callout-title-succ">✅ Resultado Esperado</div>
      <div style="font-size: 11px; color: #065f46; line-height: 1.35;">${pop.expected_result}</div>
    </div>
  `
      : ""
  }

  <div class="footer">
    <div>
      <div><b>Sempre Joias</b> &bull; Controle de Qualidade e Processos Industriais</div>
      <div>Emitido em: ${dateFormatted} às ${timeFormatted} &bull; Documento Controlado</div>
    </div>
    <div class="sig-block">
      Visto do Responsável / Data
    </div>
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 300);
    };
  </script>
</body>
</html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const totalSteps = pop.steps?.length || 0;
  const completedSteps = Object.values(checkedSteps).filter(Boolean).length;
  const isAllCompleted = totalSteps > 0 && completedSteps === totalSteps;

  return (
    <div className="w-full bg-white rounded-2xl border-2 border-indigo-100 shadow-sm overflow-hidden my-6 print:border-slate-300 print:shadow-none">
      {/* Header do POP */}
      <div className="bg-gradient-to-r from-slate-900 to-indigo-950 p-5 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300 shrink-0">
            <FileCheck size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-md bg-indigo-500 text-white font-mono text-[11px] font-bold tracking-wider uppercase shadow-xs">
                {pop.tag || "POP OPERACIONAL"}
              </span>
              {isAllCompleted && (
                <span className="px-2 py-0.5 rounded-md bg-emerald-500 text-white text-[10px] font-bold flex items-center gap-1">
                  <Check size={12} /> Executado 100%
                </span>
              )}
            </div>
            <h3 className="text-base font-bold text-white mt-1 leading-tight">
              {pop.title}
            </h3>
          </div>
        </div>

        <button
          type="button"
          onClick={handlePrint}
          className="print:hidden px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-slate-200 hover:text-white text-xs font-semibold flex items-center gap-1.5 transition self-start sm:self-center cursor-pointer"
          title="Imprimir este procedimento"
        >
          <Printer size={14} /> Imprimir POP
        </button>
      </div>

      {/* Metadados: Quando Usar, Responsável, Ferramentas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-slate-50 border-b border-slate-100 text-xs">
        <div className="flex items-start gap-2 bg-white p-2.5 rounded-xl border border-slate-200/70">
          <Calendar size={15} className="text-indigo-600 shrink-0 mt-0.5" />
          <div>
            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Quando Usar
            </span>
            <span className="text-slate-800 font-medium leading-snug">
              {pop.when_to_use}
            </span>
          </div>
        </div>

        <div className="flex items-start gap-2 bg-white p-2.5 rounded-xl border border-slate-200/70">
          <User size={15} className="text-indigo-600 shrink-0 mt-0.5" />
          <div>
            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Responsável
            </span>
            <span className="text-slate-800 font-medium leading-snug">
              {pop.responsible}
            </span>
          </div>
        </div>

        <div className="flex items-start gap-2 bg-white p-2.5 rounded-xl border border-slate-200/70">
          <Wrench size={15} className="text-indigo-600 shrink-0 mt-0.5" />
          <div>
            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Ferramentas / Insumos
            </span>
            <span className="text-slate-800 font-medium leading-snug">
              {Array.isArray(pop.tools) ? pop.tools.join(", ") : pop.tools}
            </span>
          </div>
        </div>
      </div>

      {/* Lista de Passos Numerados com Interatividade */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
            <span>Passo a Passo de Execução</span>
            <span className="text-slate-400 font-normal">({completedSteps}/{totalSteps} cumpridos)</span>
          </h4>
          {totalSteps > 0 && (
            <button
              type="button"
              onClick={() => {
                if (isAllCompleted) {
                  setCheckedSteps({});
                } else {
                  const all = {};
                  pop.steps.forEach((_, i) => (all[i] = true));
                  setCheckedSteps(all);
                }
              }}
              className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer"
            >
              {isAllCompleted ? "Desmarcar Todos" : "Marcar Todos"}
            </button>
          )}
        </div>

        <div className="space-y-2">
          {pop.steps?.map((step, idx) => {
            const isChecked = !!checkedSteps[idx];
            return (
              <div
                key={idx}
                onClick={() => toggleStep(idx)}
                className={`flex items-start gap-3 p-3 rounded-xl border transition cursor-pointer select-none ${
                  isChecked
                    ? "bg-indigo-50/50 border-indigo-200 text-slate-700"
                    : "bg-white border-slate-200 hover:border-slate-300 text-slate-800"
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 transition ${
                    isChecked
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-600 border border-slate-200"
                  }`}
                >
                  {isChecked ? <Check size={14} /> : idx + 1}
                </div>
                <div className="flex-1 text-xs sm:text-sm font-medium leading-relaxed pt-0.5">
                  <span className={isChecked ? "line-through text-slate-400" : ""}>
                    {step}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Alerta de Pontos Críticos */}
        {pop.critical_points && (
          <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs">
              <span className="font-bold block text-amber-800 uppercase tracking-wide text-[11px] mb-0.5">
                Atenção Crítica / Ponto de Falha ⚠️
              </span>
              <p className="leading-relaxed">{pop.critical_points}</p>
            </div>
          </div>
        )}

        {/* Alerta de Resultado Esperado */}
        {pop.expected_result && (
          <div className="mt-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-start gap-3">
            <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-xs">
              <span className="font-bold block text-emerald-800 uppercase tracking-wide text-[11px] mb-0.5">
                Resultado Esperado ✅
              </span>
              <p className="leading-relaxed">{pop.expected_result}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
